import { useCallback, useEffect, useRef, useState } from 'react';

import { paintVar } from './types.js';
import { useReducedMotion } from './useReducedMotion.js';

import type { StyleWithVars, WrappedSlideSpec } from './types.js';
import type { CSSProperties, PointerEvent as ReactPointerEvent, ReactElement } from 'react';

/** Long enough that a tap is never read as a hold, short enough that a hold feels immediate. */
const HOLD_MS = 220;
/** The left third goes back, as every story player on a phone does. */
const BACK_ZONE = 0.33;

type WrappedStoryProps = {
  readonly slides: readonly WrappedSlideSpec[];
  readonly label: string;
};

/**
 * `visits` counts how many times each slide has been entered. It is what the content is keyed on,
 * so arriving at a slide remounts its content — restarting counters — while the slide being left
 * keeps its own count, and so its own key, and so stays mounted long enough to fade out.
 */
type Cursor = {
  readonly index: number;
  readonly visits: readonly number[];
};

type Hold = {
  readonly timer: ReturnType<typeof setTimeout> | undefined;
  readonly isHeld: boolean;
  readonly isIgnored: boolean;
};

const IDLE_HOLD: Hold = { timer: undefined, isHeld: false, isIgnored: false };

const firstVisit = (slides: readonly WrappedSlideSpec[]): Cursor => ({
  index: 0,
  visits: slides.map((_slide, index) => (index === 0 ? 1 : 0)),
});

export const WrappedStory = ({ slides, label }: WrappedStoryProps): ReactElement | null => {
  const [cursor, setCursor] = useState<Cursor>(() => firstVisit(slides));
  const [isPausedByUser, setIsPausedByUser] = useState(false);
  const [isHeld, setIsHeld] = useState(false);
  const [announcement, setAnnouncement] = useState('');
  const isMotionReduced = useReducedMotion();

  const draftProgress = useRef({ index: 0, elapsedMs: 0 });
  const draftHold = useRef<Hold>(IDLE_HOLD);
  const draftCurrentSlide = useRef<HTMLElement | null>(null);

  const lastIndex = slides.length - 1;
  const isPaused = isPausedByUser || isHeld;
  const currentDurationMs = slides[cursor.index]?.durationMs ?? 0;

  const moveBy = useCallback(
    (step: number): void => {
      setCursor((previous) => {
        const next = Math.min(Math.max(previous.index + step, 0), slides.length - 1);

        if (next === previous.index) {
          return previous;
        }

        return {
          index: next,
          visits: previous.visits.map((visit, index) => (index === next ? visit + 1 : visit)),
        };
      });
    },
    [slides.length],
  );

  /**
   * The timer, not `animationend`, decides when a slide is over: the bar is a picture of the
   * countdown, and a picture drifts out of step with React the moment anything pauses. Elapsed
   * time is carried across pauses in a ref so a resumed slide finishes what it had left, and the
   * last slide simply holds.
   */
  useEffect(() => {
    if (draftProgress.current.index !== cursor.index) {
      draftProgress.current = { index: cursor.index, elapsedMs: 0 };
    }

    if (isPaused || isMotionReduced || cursor.index >= lastIndex) {
      return;
    }

    const startedAt = Date.now();
    const remainingMs = Math.max(0, currentDurationMs - draftProgress.current.elapsedMs);

    const timer = setTimeout(() => {
      moveBy(1);
    }, remainingMs);

    return (): void => {
      clearTimeout(timer);
      draftProgress.current = {
        index: cursor.index,
        elapsedMs: draftProgress.current.elapsedMs + (Date.now() - startedAt),
      };
    };
  }, [cursor.index, currentDurationMs, isMotionReduced, isPaused, lastIndex, moveBy]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'ArrowRight') {
        moveBy(1);

        return;
      }

      if (event.key === 'ArrowLeft') {
        moveBy(-1);

        return;
      }

      if (event.key !== ' ') {
        return;
      }

      // A focused button already answers the space bar with a press of its own.
      const focusedButton = document.activeElement?.closest('button') ?? null;

      if (focusedButton === null) {
        event.preventDefault();
        setIsPausedByUser((paused) => !paused);
      }
    };

    document.addEventListener('keydown', onKeyDown);

    return (): void => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [moveBy]);

  useEffect(() => {
    const text = draftCurrentSlide.current?.textContent ?? '';
    setAnnouncement(text.replaceAll(/\s+/gu, ' ').trim());
  }, [cursor.index]);

  const endHold = (): void => {
    const { timer, isHeld: wasHeld } = draftHold.current;

    if (timer !== undefined) {
      clearTimeout(timer);
    }

    draftHold.current = IDLE_HOLD;

    if (wasHeld) {
      setIsHeld(false);
    }
  };

  const onPointerDown = (event: ReactPointerEvent<HTMLElement>): void => {
    const { target } = event;
    const isIgnored = target instanceof Element && target.closest('button, .controls') !== null;

    if (isIgnored) {
      draftHold.current = { ...IDLE_HOLD, isIgnored };

      return;
    }

    const timer = setTimeout(() => {
      draftHold.current = { ...draftHold.current, isHeld: true };
      setIsHeld(true);
    }, HOLD_MS);

    draftHold.current = { ...IDLE_HOLD, timer };
  };

  const onPointerUp = (event: ReactPointerEvent<HTMLElement>): void => {
    const { isHeld: wasHeld, isIgnored } = draftHold.current;
    endHold();

    // A hold is a pause, not a tap: releasing it must not also turn the page.
    if (isIgnored || wasHeld) {
      return;
    }

    const rect = event.currentTarget.getBoundingClientRect();
    moveBy(event.clientX - rect.left < rect.width * BACK_ZONE ? -1 : 1);
  };

  const current = slides[cursor.index];

  if (current === undefined) {
    return null;
  }

  const storyStyle: StyleWithVars = {
    '--slide-bg': paintVar(current.bg),
    '--slide-ink': paintVar(current.ink),
  };

  return (
    <div className="wrapped stage">
      <section
        className={`story${isPaused ? ' paused' : ''}`}
        style={storyStyle}
        aria-roledescription="story"
        aria-label={label}
      >
        <div className="backdrop" aria-hidden="true">
          {current.blobs.map((blob, index) => {
            const blobStyle: StyleWithVars = {
              '--x': blob.x,
              '--y': blob.y,
              '--s': String(blob.scale),
            };
            const inkStyle: StyleWithVars = { '--c': paintVar(blob.paint) };

            return (
              <div className="blob" key={`blob-${String(index)}`} style={blobStyle}>
                <i style={inkStyle} />
              </div>
            );
          })}
          <div className="grain" />
        </div>

        <div className="bars" aria-hidden="true">
          {slides.map((slide, index) => {
            const isRunning = index === cursor.index && !isMotionReduced;
            const isDone = index < cursor.index || (index === cursor.index && isMotionReduced);
            const fillStyle: CSSProperties = {
              animationDuration: `${String(slide.durationMs)}ms`,
            };

            return (
              <span
                key={slide.id}
                className={`bar${isDone ? ' is-done' : ''}${isRunning ? ' is-running' : ''}`}
              >
                <span
                  key={cursor.visits[index] ?? 0}
                  className="fill"
                  style={isRunning ? fillStyle : {}}
                />
              </span>
            );
          })}
        </div>

        {/* The tap zone wraps the slides rather than the whole card so the controls below stay
            ordinary buttons, and `role="presentation"` because it is a gesture surface, not a
            thing in the accessibility tree — the keyboard reaches the same actions via the
            controls and the document-level arrow keys. */}
        <div
          className="tapzone"
          role="presentation"
          onPointerDown={onPointerDown}
          onPointerUp={onPointerUp}
          onPointerCancel={endHold}
          onContextMenu={(event): void => {
            event.preventDefault();
          }}
        >
          <div className="slides">
            {slides.map((slide, index) => {
              const isCurrent = index === cursor.index;
              const visit = cursor.visits[index] ?? 0;

              return (
                <section
                  key={slide.id}
                  ref={isCurrent ? draftCurrentSlide : null}
                  className={`slide${isCurrent ? ' is-current' : ''}`}
                  aria-label={`Slide ${String(index + 1)} of ${String(slides.length)}`}
                  aria-hidden={!isCurrent}
                  inert={!isCurrent}
                >
                  {visit > 0 ? (
                    <div className="slide-body" key={visit}>
                      {slide.content}
                    </div>
                  ) : null}
                </section>
              );
            })}
          </div>
        </div>

        <div className="controls">
          <button
            type="button"
            className="pill"
            onClick={(): void => {
              moveBy(-1);
            }}
            disabled={cursor.index === 0}
          >
            Back
          </button>
          <button
            type="button"
            className="pill"
            onClick={(): void => {
              setIsPausedByUser((paused) => !paused);
            }}
          >
            {isPausedByUser ? 'Play' : 'Pause'}
          </button>
          <button
            type="button"
            className="pill"
            onClick={(): void => {
              moveBy(1);
            }}
            disabled={cursor.index === lastIndex}
          >
            Next
          </button>
        </div>

        <p className="vh" aria-live="polite">
          {announcement}
        </p>
      </section>
    </div>
  );
};
